import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    FiArrowLeft,
    FiBriefcase,
    FiCalendar,
    FiCheckCircle,
    FiEdit2,
    FiGlobe,
    FiMail,
    FiMapPin,
    FiMonitor,
    FiRefreshCw,
    FiShield,
    FiShoppingBag,
    FiSlash,
    FiSmartphone,
    FiUser,
} from "react-icons/fi";

import useSwal
    from "../../../../../hooks/useSwal";

import {
    fetchPlatformBusinessById,
    updatePlatformBusiness,
    activatePlatformBusiness,
    suspendPlatformBusiness,

    selectSelectedPlatformBusiness,
    selectPlatformBusinessDetailsLoading,
    selectPlatformBusinessUpdateLoading,
    selectPlatformBusinessStatusLoading,
    selectPlatformBusinessStatusBusinessId,

    clearSelectedPlatformBusiness,
} from "../../../../../store/platformBusiness/platformBusinessSlice";


import {
    BusinessStatusBadge,
    BusinessFeatureTags,
    BusinessModal,
} from "./components";

import PlatformBusinessForm
    from "./PlatformBusinessForm";

import "./PlatformBusiness.css";


/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {

    if (
        !value
    ) {

        return "-";

    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "-";

    }


    return date.toLocaleDateString(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",

            hour:
                "2-digit",

            minute:
                "2-digit",
        }
    );

};


/*
|--------------------------------------------------------------------------
| GET BUSINESS ID
|--------------------------------------------------------------------------
*/

const getBusinessId = (
    business
) => {

    return (
        business?._id ||
        business?.id ||
        null
    );

};


/*
|--------------------------------------------------------------------------
| ADDRESS TEXT
|--------------------------------------------------------------------------
*/

const getAddressText = (
    address
) => {

    if (
        !address
    ) {

        return "-";

    }


    const parts = [
        address?.line1,
        address?.line2,
        address?.city,
        address?.state,
        address?.pincode,
        address?.country,
    ]
        .filter(
            Boolean
        )
        .map(
            (
                item
            ) =>
                String(
                    item
                ).trim()
        )
        .filter(
            Boolean
        );


    return parts.length
        ? parts.join(
            ", "
        )
        : "-";

};


/*
|--------------------------------------------------------------------------
| DETAIL ITEM
|--------------------------------------------------------------------------
*/

const DetailItem = ({
    icon: Icon,

    label,

    value,

    children,
}) => {

    return (

        <div className="platform-business-detail-item">

            <div className="platform-business-detail-icon">

                {
                    Icon
                        ? (
                            <Icon />
                        )
                        : null
                }

            </div>


            <div className="platform-business-detail-content">

                <span>

                    {
                        label
                    }

                </span>


                {
                    children
                        ? children
                        : (

                            <strong>

                                {
                                    value ||
                                    "-"
                                }

                            </strong>

                        )
                }

            </div>

        </div>

    );

};


/*
|--------------------------------------------------------------------------
| PLATFORM BUSINESS DETAILS
|--------------------------------------------------------------------------
*/

const PlatformBusinessDetails =
    () => {

        const dispatch =
            useDispatch();


        const history =
            useHistory();


        const {
            id,
        } =
            useParams();


        const swal =
            useSwal();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const business =
            useSelector(
                selectSelectedPlatformBusiness
            );


        const detailsLoading =
            useSelector(
                selectPlatformBusinessDetailsLoading
            );


        const updateLoading =
            useSelector(
                selectPlatformBusinessUpdateLoading
            );


        const statusLoading =
            useSelector(
                selectPlatformBusinessStatusLoading
            );


        const statusBusinessId =
            useSelector(
                selectPlatformBusinessStatusBusinessId
            );


        /*
        |--------------------------------------------------------------------------
        | EDIT MODAL
        |--------------------------------------------------------------------------
        */

        const [
            showEditModal,
            setShowEditModal,
        ] =
            useState(
                false
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD BUSINESS
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                if (
                    id
                ) {

                    dispatch(
                        fetchPlatformBusinessById(
                            id
                        )
                    );

                }


                return () => {

                    dispatch(
                        clearSelectedPlatformBusiness()
                    );

                };

            },
            [
                dispatch,
                id,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | DERIVED VALUES
        |--------------------------------------------------------------------------
        */

        const currentBusinessId =
            getBusinessId(
                business
            );


        const status =
            String(
                business?.status ||
                ""
            )
                .trim()
                .toUpperCase();


        const isStatusUpdating =
            statusLoading &&
            String(
                statusBusinessId
            ) ===
                String(
                    currentBusinessId
                );


        const owner =
            business?.owner ||
            null;


        const addressText =
            useMemo(
                () =>
                    getAddressText(
                        business?.address
                    ),
                [
                    business?.address,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

        const handleRefresh =
            () => {

                if (
                    !id
                ) {

                    return;

                }


                dispatch(
                    fetchPlatformBusinessById(
                        id
                    )
                );

            };


        /*
        |--------------------------------------------------------------------------
        | OPEN EDIT
        |--------------------------------------------------------------------------
        */

        const handleOpenEdit =
            () => {

                if (
                    !business
                ) {

                    return;

                }


                setShowEditModal(
                    true
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CLOSE EDIT
        |--------------------------------------------------------------------------
        */

        const handleCloseEdit =
            () => {

                if (
                    updateLoading
                ) {

                    return;

                }


                setShowEditModal(
                    false
                );

            };


        /*
        |--------------------------------------------------------------------------
        | UPDATE BUSINESS
        |--------------------------------------------------------------------------
        */

        const handleUpdate =
            async (
                payload
            ) => {

                if (
                    !id
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        updatePlatformBusiness({
                            id,

                            data:
                                payload,
                        })
                    );


                if (
                    updatePlatformBusiness
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.success({
                        title:
                            "Business Updated",

                        text:
                            "Business information updated successfully.",
                    });


                    setShowEditModal(
                        false
                    );


                    dispatch(
                        fetchPlatformBusinessById(
                            id
                        )
                    );


                    return;

                }


                swal.error({
                    title:
                        "Update Failed",

                    text:
                        result?.payload
                            ?.message ||
                        "Unable to update business.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | ACTIVATE
        |--------------------------------------------------------------------------
        */

        const handleActivate =
            async () => {

                if (
                    !currentBusinessId ||
                    isStatusUpdating
                ) {

                    return;

                }


                const confirmation =
                    await swal.confirm({
                        title:
                            "Activate Business?",

                        text:
                            `${
                                business?.name ||
                                "This business"
                            } will regain access to tenant services.`,

                        confirmButtonText:
                            "Activate",
                    });


                if (
                    confirmation
                        ?.isConfirmed ===
                    false
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        activatePlatformBusiness(
                            currentBusinessId
                        )
                    );


                if (
                    activatePlatformBusiness
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.success({
                        title:
                            "Business Activated",

                        text:
                            "Business activated successfully.",
                    });


                    dispatch(
                        fetchPlatformBusinessById(
                            id
                        )
                    );


                    return;

                }


                swal.error({
                    title:
                        "Activation Failed",

                    text:
                        result?.payload
                            ?.message ||
                        "Unable to activate business.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | SUSPEND
        |--------------------------------------------------------------------------
        */

        const handleSuspend =
            async () => {

                if (
                    !currentBusinessId ||
                    isStatusUpdating
                ) {

                    return;

                }


                const confirmation =
                    await swal.confirm({
                        title:
                            "Suspend Business?",

                        text:
                            `${
                                business?.name ||
                                "This business"
                            } will lose access to tenant services until reactivated.`,

                        confirmButtonText:
                            "Suspend",
                    });


                if (
                    confirmation
                        ?.isConfirmed ===
                    false
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        suspendPlatformBusiness(
                            currentBusinessId
                        )
                    );


                if (
                    suspendPlatformBusiness
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.success({
                        title:
                            "Business Suspended",

                        text:
                            "Business suspended successfully.",
                    });


                    dispatch(
                        fetchPlatformBusinessById(
                            id
                        )
                    );


                    return;

                }


                swal.error({
                    title:
                        "Suspension Failed",

                    text:
                        result?.payload
                            ?.message ||
                        "Unable to suspend business.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | LOADING
        |--------------------------------------------------------------------------
        */

        if (
            detailsLoading &&
            !business
        ) {

            return (


                    <div className="platform-business-details-state-page">

                        <div className="platform-business-details-loading">

                            <div className="platform-business-loader" />


                            <strong>

                                Loading Business

                            </strong>


                            <span>

                                Fetching tenant information...

                            </span>

                        </div>

                    </div>


            );

        }


        /*
        |--------------------------------------------------------------------------
        | NOT FOUND
        |--------------------------------------------------------------------------
        */

        if (
            !business &&
            !detailsLoading
        ) {

            return (


                    <div className="platform-business-details-state-page">

                        <div className="platform-business-details-empty">

                            <FiBriefcase />


                            <h2>

                                Business Not Found

                            </h2>


                            <p>

                                The requested business could not be loaded.

                            </p>


                            <button
                                type="button"

                                onClick={() =>
                                    history.push(
                                        "/platform/businesses"
                                    )
                                }
                            >

                                <FiArrowLeft />

                                Back to Businesses

                            </button>

                        </div>

                    </div>


            );

        }


        /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

        return (


                <div className="platform-business-page platform-business-details-page">

                    {/*
                    |--------------------------------------------------------------------------
                    | HEADER
                    |--------------------------------------------------------------------------
                    */}

                    <header className="platform-business-details-header">

                        <div>

                            <button
                                type="button"

                                className="platform-business-back-button"

                                onClick={() =>
                                    history.push(
                                        "/platform/businesses"
                                    )
                                }
                            >

                                <FiArrowLeft />

                                Businesses

                            </button>


                            <div className="platform-business-details-title-row">

                                <div className="platform-business-details-avatar">

                                    {
                                        String(
                                            business?.name ||
                                            "B"
                                        )
                                            .charAt(
                                                0
                                            )
                                            .toUpperCase()
                                    }

                                </div>


                                <div>

                                    <div className="platform-business-details-title">

                                        <h1>

                                            {
                                                business?.name ||
                                                "Business"
                                            }

                                        </h1>


                                        <BusinessStatusBadge
                                            status={
                                                business?.status
                                            }
                                        />

                                    </div>


                                    <p>

                                        {
                                            business?.legalName ||
                                            business?.code ||
                                            "-"
                                        }

                                    </p>

                                </div>

                            </div>

                        </div>


                        <div className="platform-business-details-actions">

                            <button
                                type="button"

                                className="platform-business-refresh-button"

                                onClick={
                                    handleRefresh
                                }

                                disabled={
                                    detailsLoading
                                }
                            >

                                <FiRefreshCw
                                    className={
                                        detailsLoading
                                            ? "platform-business-spin"
                                            : ""
                                    }
                                />

                                {
                                    detailsLoading
                                        ? "Refreshing..."
                                        : "Refresh"
                                }

                            </button>


                            <button
                                type="button"

                                className="platform-business-edit-button"

                                onClick={
                                    handleOpenEdit
                                }

                                disabled={
                                    updateLoading ||
                                    isStatusUpdating
                                }
                            >

                                <FiEdit2 />

                                Edit Business

                            </button>


                            {
                                status ===
                                "ACTIVE"
                                    ? (

                                        <button
                                            type="button"

                                            className="platform-business-suspend-button"

                                            onClick={
                                                handleSuspend
                                            }

                                            disabled={
                                                isStatusUpdating
                                            }
                                        >

                                            <FiSlash />


                                            {
                                                isStatusUpdating
                                                    ? "Suspending..."
                                                    : "Suspend"
                                            }

                                        </button>

                                    )
                                    : (

                                        <button
                                            type="button"

                                            className="platform-business-activate-button"

                                            onClick={
                                                handleActivate
                                            }

                                            disabled={
                                                isStatusUpdating
                                            }
                                        >

                                            <FiCheckCircle />


                                            {
                                                isStatusUpdating
                                                    ? "Activating..."
                                                    : "Activate"
                                            }

                                        </button>

                                    )
                            }

                        </div>

                    </header>


                    {/*
                    |--------------------------------------------------------------------------
                    | OVERVIEW
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-business-details-overview">

                        <article>

                            <span>
                                Business Code
                            </span>


                            <strong className="platform-business-details-code">

                                {
                                    business?.code ||
                                    "-"
                                }

                            </strong>

                        </article>


                        <article>

                            <span>
                                Subscription
                            </span>


                            <BusinessStatusBadge
                                status={
                                    business?.subscriptionStatus
                                }
                            />

                        </article>


                        <article>

                            <span>
                                Created
                            </span>


                            <strong>

                                {
                                    formatDate(
                                        business?.createdAt
                                    )
                                }

                            </strong>

                        </article>


                        <article>

                            <span>
                                Last Updated
                            </span>


                            <strong>

                                {
                                    formatDate(
                                        business?.updatedAt
                                    )
                                }

                            </strong>

                        </article>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | DETAILS GRID
                    |--------------------------------------------------------------------------
                    */}

                    <div className="platform-business-details-grid">

                        {/*
                        |--------------------------------------------------------------------------
                        | BUSINESS INFORMATION
                        |--------------------------------------------------------------------------
                        */}

                        <section className="platform-business-details-card">

                            <div className="platform-business-details-card-header">

                                <div>
                                    <FiBriefcase />
                                </div>


                                <div>

                                    <h2>
                                        Business Information
                                    </h2>


                                    <p>
                                        Tenant identity and contact information.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-business-detail-items">

                                <DetailItem
                                    icon={
                                        FiBriefcase
                                    }

                                    label="Business Name"

                                    value={
                                        business?.name
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiBriefcase
                                    }

                                    label="Legal Name"

                                    value={
                                        business?.legalName
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiSmartphone
                                    }

                                    label="Mobile"

                                    value={
                                        business?.mobile
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiMail
                                    }

                                    label="Email"

                                    value={
                                        business?.email
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiGlobe
                                    }

                                    label="Subdomain"

                                    value={
                                        business?.subdomain
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiGlobe
                                    }

                                    label="Custom Domain"

                                    value={
                                        business?.customDomain
                                    }
                                />

                            </div>

                        </section>


                        {/*
                        |--------------------------------------------------------------------------
                        | OWNER
                        |--------------------------------------------------------------------------
                        */}

                        <section className="platform-business-details-card">

                            <div className="platform-business-details-card-header">

                                <div>
                                    <FiUser />
                                </div>


                                <div>

                                    <h2>
                                        Business Owner
                                    </h2>


                                    <p>
                                        Primary OWNER account for this tenant.
                                    </p>

                                </div>

                            </div>


                            {
                                owner
                                    ? (

                                        <div className="platform-business-owner-card">

                                            <div className="platform-business-owner-avatar">

                                                {
                                                    String(
                                                        owner?.name ||
                                                        "O"
                                                    )
                                                        .charAt(
                                                            0
                                                        )
                                                        .toUpperCase()
                                                }

                                            </div>


                                            <div className="platform-business-owner-main">

                                                <div>

                                                    <strong>

                                                        {
                                                            owner?.name ||
                                                            "-"
                                                        }

                                                    </strong>


                                                    <BusinessStatusBadge
                                                        status={
                                                            owner?.status
                                                        }
                                                    />

                                                </div>


                                                <span>
                                                    OWNER ACCOUNT
                                                </span>

                                            </div>


                                            <div className="platform-business-owner-details">

                                                <DetailItem
                                                    icon={
                                                        FiSmartphone
                                                    }

                                                    label="Mobile"

                                                    value={
                                                        owner?.mobile
                                                    }
                                                />


                                                <DetailItem
                                                    icon={
                                                        FiMail
                                                    }

                                                    label="Email"

                                                    value={
                                                        owner?.email
                                                    }
                                                />


                                                <DetailItem
                                                    icon={
                                                        FiCalendar
                                                    }

                                                    label="Last Login"

                                                    value={
                                                        formatDate(
                                                            owner?.lastLogin
                                                        )
                                                    }
                                                />

                                            </div>

                                        </div>

                                    )
                                    : (

                                        <div className="platform-business-details-no-owner">

                                            <FiUser />


                                            <strong>
                                                No owner found
                                            </strong>


                                            <span>
                                                This tenant currently has no OWNER account.
                                            </span>

                                        </div>

                                    )
                            }

                        </section>


                        {/*
                        |--------------------------------------------------------------------------
                        | ADDRESS
                        |--------------------------------------------------------------------------
                        */}

                        <section className="platform-business-details-card">

                            <div className="platform-business-details-card-header">

                                <div>
                                    <FiMapPin />
                                </div>


                                <div>

                                    <h2>
                                        Registered Address
                                    </h2>


                                    <p>
                                        Business location and tax identity.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-business-detail-items">

                                <DetailItem
                                    icon={
                                        FiMapPin
                                    }

                                    label="Address"

                                    value={
                                        addressText
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiShield
                                    }

                                    label="GST Number"

                                    value={
                                        business?.gstNumber
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiShield
                                    }

                                    label="PAN Number"

                                    value={
                                        business?.panNumber
                                    }
                                />

                            </div>

                        </section>


                        {/*
                        |--------------------------------------------------------------------------
                        | SUBSCRIPTION & FEATURES
                        |--------------------------------------------------------------------------
                        */}

                        <section className="platform-business-details-card">

                            <div className="platform-business-details-card-header">

                                <div>
                                    <FiShoppingBag />
                                </div>


                                <div>

                                    <h2>
                                        Subscription & Features
                                    </h2>


                                    <p>
                                        Tenant access and enabled platform capabilities.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-business-detail-items">

                                <DetailItem
                                    icon={
                                        FiShoppingBag
                                    }

                                    label="Subscription Status"
                                >

                                    <BusinessStatusBadge
                                        status={
                                            business?.subscriptionStatus
                                        }
                                    />

                                </DetailItem>


                                <DetailItem
                                    icon={
                                        FiCalendar
                                    }

                                    label="Trial Ends"

                                    value={
                                        formatDate(
                                            business?.trialEndsAt
                                        )
                                    }
                                />


                                <DetailItem
                                    icon={
                                        FiMonitor
                                    }

                                    label="Enabled Features"
                                >

                                    <BusinessFeatureTags
                                        storefrontEnabled={
                                            business?.storefrontEnabled
                                        }

                                        marketplaceEnabled={
                                            business?.marketplaceEnabled
                                        }
                                    />

                                </DetailItem>

                            </div>

                        </section>


                        {/*
                        |--------------------------------------------------------------------------
                        | BRANDING
                        |--------------------------------------------------------------------------
                        */}

                        <section className="platform-business-details-card platform-business-details-wide">

                            <div className="platform-business-details-card-header">

                                <div>
                                    <FiMonitor />
                                </div>


                                <div>

                                    <h2>
                                        Branding
                                    </h2>


                                    <p>
                                        Tenant-specific visual configuration.
                                    </p>

                                </div>

                            </div>


                            <div className="platform-business-branding-grid">

                                <div>

                                    <span>
                                        Logo
                                    </span>


                                    {
                                        business?.branding
                                            ?.logo
                                            ? (

                                                <img
                                                    src={
                                                        business
                                                            .branding
                                                            .logo
                                                    }

                                                    alt="Business logo"
                                                />

                                            )
                                            : (

                                                <strong>
                                                    Not configured
                                                </strong>

                                            )
                                    }

                                </div>


                                <div>

                                    <span>
                                        Favicon
                                    </span>


                                    {
                                        business?.branding
                                            ?.favicon
                                            ? (

                                                <img
                                                    src={
                                                        business
                                                            .branding
                                                            .favicon
                                                    }

                                                    alt="Business favicon"
                                                />

                                            )
                                            : (

                                                <strong>
                                                    Not configured
                                                </strong>

                                            )
                                    }

                                </div>


                                <div>

                                    <span>
                                        Primary Color
                                    </span>


                                    <div className="platform-business-color-value">

                                        {
                                            business?.branding
                                                ?.primaryColor
                                                ? (

                                                    <i
                                                        style={{
                                                            background:
                                                                business
                                                                    .branding
                                                                    .primaryColor,
                                                        }}
                                                    />

                                                )
                                                : null
                                        }


                                        <strong>

                                            {
                                                business?.branding
                                                    ?.primaryColor ||
                                                "-"
                                            }

                                        </strong>

                                    </div>

                                </div>


                                <div>

                                    <span>
                                        Secondary Color
                                    </span>


                                    <div className="platform-business-color-value">

                                        {
                                            business?.branding
                                                ?.secondaryColor
                                                ? (

                                                    <i
                                                        style={{
                                                            background:
                                                                business
                                                                    .branding
                                                                    .secondaryColor,
                                                        }}
                                                    />

                                                )
                                                : null
                                        }


                                        <strong>

                                            {
                                                business?.branding
                                                    ?.secondaryColor ||
                                                "-"
                                            }

                                        </strong>

                                    </div>

                                </div>

                            </div>

                        </section>

                    </div>


                    {/*
                    |--------------------------------------------------------------------------
                    | EDIT MODAL
                    |--------------------------------------------------------------------------
                    */}

                    <BusinessModal
                        open={
                            showEditModal
                        }

                        title="Edit Business"

                        description="Update tenant information, subscription and enabled features."

                        onClose={
                            handleCloseEdit
                        }

                        disableClose={
                            updateLoading
                        }
                    >

                        <div className="platform-business-modal-body">

                            <PlatformBusinessForm
                                mode="edit"

                                initialData={
                                    business
                                }

                                loading={
                                    updateLoading
                                }

                                onSubmit={
                                    handleUpdate
                                }

                                onCancel={
                                    handleCloseEdit
                                }
                            />

                        </div>

                    </BusinessModal>

                </div>


        );

    };


export default PlatformBusinessDetails;